<template>
  <div class="d-flex align-items-center justify-content-center px-1" style="min-height: 100vh">
    <b-card class="text-center shadow-sm" style="max-width: 520px; width: 100%">
      <!-- Loading -->
      <div v-if="loading" class="py-2">
        <b-spinner variant="primary" />
        <p class="text-muted mt-1 mb-0">Loading your invitation…</p>
      </div>

      <!-- Could not load / not found -->
      <div v-else-if="!invite" class="py-1">
        <feather-icon icon="AlertTriangleIcon" size="48" class="text-danger mb-1" />
        <h3 class="mb-50">Invitation unavailable</h3>
        <p class="text-muted mb-0">{{ error || 'This invitation could not be found.' }}</p>
      </div>

      <!-- Just returned from the provider, or already accepted -->
      <div v-else-if="returnedFromProvider || invite.status === 'accepted'" class="py-1">
        <feather-icon icon="CheckCircleIcon" size="48" class="text-success mb-1" />
        <h3 class="mb-50">Mailbox connected</h3>
        <p class="text-muted mb-0">
          Thanks, {{ invite.name }}! Your mailbox <strong>{{ invite.email }}</strong> is now connected. Syncing may
          take a few minutes to finish — there's nothing more you need to do.
        </p>
      </div>

      <!-- Revoked / expired -->
      <div v-else-if="invite.status === 'revoked' || invite.status === 'expired'" class="py-1">
        <feather-icon icon="SlashIcon" size="48" class="text-warning mb-1" />
        <h3 class="mb-50">Invitation {{ invite.status }}</h3>
        <p class="text-muted mb-0">
          This invitation is no longer valid. Please contact {{ invite.organization }} for a new link.
        </p>
      </div>

      <!-- Pending — ready to connect -->
      <div v-else class="py-1">
        <feather-icon icon="MailIcon" size="48" class="text-primary mb-1" />
        <h3 class="mb-50">Connect your mailbox</h3>
        <p class="text-muted">
          Hi <strong>{{ invite.name }}</strong
          >, {{ invite.organization }} has invited you to securely connect your Outlook mailbox
          (<strong>{{ invite.email }}</strong
          >). You don't need an account.
        </p>

        <b-alert v-if="error" variant="danger" show class="text-left">
          <div class="alert-body">{{ error }}</div>
        </b-alert>

        <b-button variant="primary" size="lg" block :disabled="connecting" @click="connect">
          <b-spinner v-if="connecting" small class="mr-50" />
          <feather-icon v-else icon="MailIcon" class="mr-50" />
          <span>Connect Outlook mailbox</span>
        </b-button>
        <p class="text-muted small mt-1 mb-0">
          You'll be redirected to Microsoft to authorize access, then returned here.
        </p>
      </div>
    </b-card>
  </div>
</template>

<script>
import { BCard, BButton, BSpinner, BAlert } from 'bootstrap-vue';
import store from '@/store';
import mailAccountStoreModule from '../mailAccountStoreModule';

const STORE_MODULE_NAME = 'mailAccountStoreModule';

export default {
  components: { BCard, BButton, BSpinner, BAlert },
  data: () => ({
    loading: true,
    connecting: false,
    invite: null,
    error: null,
  }),
  computed: {
    // EmailEngine appends ?account=... when it returns the invitee here after OAuth.
    returnedFromProvider() {
      return Boolean(this.$route.query.account) && !this.$route.query.error;
    },
  },
  created() {
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, mailAccountStoreModule);
    this.loadInvite();
  },
  beforeDestroy() {
    if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME);
  },
  methods: {
    loadInvite() {
      const { token } = this.$route.params;
      this.$store
        .dispatch(`${STORE_MODULE_NAME}/fetchPublicInvite`, { token })
        .then((response) => {
          this.invite = response.data;
        })
        .catch((err) => {
          this.error = err?.response?.data?.message || err.message;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    connect() {
      this.connecting = true;
      this.error = null;
      const { token } = this.$route.params;
      this.$store
        .dispatch(`${STORE_MODULE_NAME}/startEnrollment`, { token })
        .then((response) => {
          const { url } = response.data || {};
          if (url) {
            window.location.href = url;
            return;
          }
          this.error = 'The provider did not return an authorization link. Please try again shortly.';
          this.connecting = false;
        })
        .catch((err) => {
          this.error = err?.response?.data?.message || err.message;
          this.connecting = false;
        });
    },
  },
};
</script>
